import React, { useState, useEffect } from 'react';
import './SetTagPopup.css';
import { database } from '../firebaseConfig';
import { ref, get, update } from 'firebase/database';

const SetTagPopup = ({ 
  email, 
  userId, 
  onClose, 
  onTagsUpdated 
}) => {
  const [availableTags, setAvailableTags] = useState([]);
  const [currentTags, setCurrentTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Hilfsfunktion für konsistente E-Mail-ID
  const getEmailId = () => {
    return email.id || email.messageId || `temp_${Date.now()}`;
  };

  // Lade verfügbare Tags und aktuelle Tags
  useEffect(() => {
    const loadTagsData = async () => {
      try {
        setLoading(true);
        
        // Lade verfügbare AI Tags aus der Datenbank
        const aiTagsRef = ref(database, `/World/${userId}/settings/aiTags`);
        const snapshot = await get(aiTagsRef);
        
        const availableTagsList = [];
        if (snapshot.exists()) {
          snapshot.forEach((childSnapshot) => {
            availableTagsList.push({
              id: childSnapshot.key,
              ...childSnapshot.val()
            });
          });
        }
        
        setAvailableTags(availableTagsList);
        
        // Bereite aktuelle Tags vor
        const emailTags = email.tags || [];
        const currentTagsList = Array.isArray(emailTags) 
          ? emailTags 
          : Object.values(emailTags || {});
          
        setCurrentTags(currentTagsList.map(tag => 
          typeof tag === 'string' ? { tag: tag, name: tag } : tag
        ));
        
      } catch (err) {
        console.error('Error loading tags:', err);
        setError('Failed to load tags');
      } finally {
        setLoading(false);
      }
    };

    if (userId && email) {
      loadTagsData();
    }
  }, [userId, email]);

  // Tag hinzufügen/auswählen (Single-Select)
  const handleSelectTag = (tagToSelect) => {
    // Single-Select: Ersetze alle aktuellen Tags mit dem neuen Tag
    setCurrentTags([tagToSelect]);
  };

  // Tag entfernen
  const handleRemoveTag = () => {
    setCurrentTags([]);
  };

  // Tags speichern
  const handleSaveTags = async () => {
    try {
      setSaving(true);
      setError('');
      
      const emailId = getEmailId();
      
      // Bestimme den korrekten Pfad basierend auf dem Email-Typ
      let emailPath;
      if (email.isSent || email.emailType) {
        emailPath = `/World/${userId}/sentEmails/${emailId}`;
      } else if (email.isSpam) {
        emailPath = `/World/${userId}/spam/${emailId}`;
      } else {
        emailPath = `/World/${userId}/inbox/${emailId}`;
      }

      console.log(`🏷️ [SET-TAG] Saving tags for email ${emailId} at path: ${emailPath}`);
      console.log(`🏷️ [SET-TAG] Tags to save:`, currentTags);

      // Konvertiere Tags ins richtige Format für die Datenbank
      let tagsToSave = null;
      if (currentTags.length > 0) {
        // Speichere als Objekt mit Index-Keys (wie es ursprünglich war)
        tagsToSave = {};
        currentTags.forEach((tag, index) => {
          tagsToSave[index] = {
            id: tag.id,
            tag: tag.tag,
            name: tag.name || tag.tag,
            color: tag.color,
            icon: tag.icon,
            createdAt: new Date().toISOString()
          };
        });
      }
      
      // Update in der Datenbank
      await update(ref(database, emailPath), {
        tags: tagsToSave
      });
      
      console.log(`🏷️ [SET-TAG] Tags successfully saved for email ${emailId}`);
      
      // Callback aufrufen um Parent-Komponente zu informieren
      if (onTagsUpdated) {
        onTagsUpdated(emailId, tagsToSave);
      }
      
      // Popup schließen
      onClose();
      
    } catch (err) {
      console.error('🏷️ [SET-TAG] Error saving tags:', err);
      setError('Failed to save tags: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Alle Tags entfernen
  const handleRemoveAllTags = () => {
    setCurrentTags([]);
  };

  if (loading) {
    return (
      <div className="setTagPopupOverlay">
        <div className="setTagPopupContainer">
          <div className="setTagPopupLoading">
            <div className="setTagPopupSpinner"></div>
            <p>Loading tags...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="setTagPopupOverlay" onClick={onClose}>
      <div className="setTagPopupContainer" onClick={(e) => e.stopPropagation()}>
        <div className="setTagPopupHeader">
          <h3>Select Email Tag</h3>
          <button 
            className="setTagPopupCloseBtn" 
            onClick={onClose}
            title="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="setTagPopupBody">
          {error && (
            <div className="setTagPopupError">
              {error}
            </div>
          )}

          {/* Aktuelle Tags Sektion */}
          <div className="setTagPopupSection">
            <div className="setTagPopupSectionHeader">
              <h4>Selected Tag</h4>
              {currentTags.length > 0 && (
                <button 
                  className="setTagPopupRemoveAllBtn"
                  onClick={handleRemoveAllTags}
                >
                  Remove Tag
                </button>
              )}
            </div>
            <div className="setTagPopupCurrentTags">
              {currentTags.length > 0 ? (
                currentTags.map((tag, index) => (
                  <div 
                    key={index} 
                    className="setTagPopupCurrentTag"
                    style={{
                      backgroundColor: tag.color || '#666',
                      color: '#ffffff'
                    }}
                  >
                    {tag.icon && (
                      <img 
                        src={tag.icon} 
                        alt=""
                        className="setTagPopupTagIcon"
                      />
                    )}
                    <span>{tag.tag || tag.name}</span>
                    <button
                      className="setTagPopupRemoveTagBtn"
                      onClick={() => handleRemoveTag()}
                      title="Remove tag"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                ))
              ) : (
                <p className="setTagPopupNoTags">No tag selected</p>
              )}
            </div>
          </div>

          {/* Verfügbare Tags Sektion */}
          <div className="setTagPopupSection">
            <h4>Available Tags (Select One)</h4>
            <div className="setTagPopupAvailableTags">
              {availableTags.length > 0 ? (
                availableTags.map((tag) => {
                  const isSelected = currentTags.some(currentTag => 
                    (currentTag.tag || currentTag.name) === tag.tag
                  );
                  
                  return (
                    <button
                      key={tag.id}
                      className={`setTagPopupAvailableTag ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelectTag(tag)}
                      style={{
                        backgroundColor: tag.color || '#666',
                        color: '#ffffff',
                        opacity: isSelected ? 1 : 0.8,
                        border: isSelected ? '2px solid #fff' : '1px solid transparent'
                      }}
                    >
                      {tag.icon && (
                        <img 
                          src={tag.icon} 
                          alt=""
                          className="setTagPopupTagIcon"
                        />
                      )}
                      <span>{tag.tag || tag.name}</span>
                      {isSelected && (
                        <span className="setTagPopupSelected">✓</span>
                      )}
                    </button>
                  );
                })
              ) : (
                <p className="setTagPopupNoAvailableTags">No tags available. Create tags in settings first.</p>
              )}
            </div>
          </div>
        </div>

        <div className="setTagPopupFooter">
          <button 
            className="setTagPopupCancelBtn" 
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button 
            className="setTagPopupSaveBtn" 
            onClick={handleSaveTags}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetTagPopup; 